import { type TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { request } from "graphql-request";
import config from "src/config";
import { useAccessTokenBasic } from "../use-access-token";

// Conditionally check if TVariables is an empty object
// so that the variables property becomes optional
// if there are no variables
type UseGqlQueryArgs<TResult, TVariables> = TVariables extends Record<
  string,
  never
>
  ? {
      query: TypedDocumentNode<TResult, TVariables>;
      variables?: undefined;
    }
  : {
      query: TypedDocumentNode<TResult, TVariables>;
      variables: TVariables;
    };

export function useGqlQuery<TResult, TVariables>({
  query,
  variables,
  ...options
}: UseGqlQueryArgs<TResult, TVariables> &
  Omit<
    UseQueryOptions<unknown, Error, TResult, Array<unknown>>,
    "queryKey" | "queryFn"
  >): UseQueryResult<TResult> {
  const accessToken = useAccessTokenBasic();
  return useQuery({
    ...options,
    enabled: accessToken != null && options.enabled !== false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    queryKey: [(query.definitions[0] as any)?.name?.value, variables],
    queryFn: ({ queryKey }) =>
      request(
        config.publicApiUrl,
        query,
        queryKey[1] == null ? undefined : queryKey[1],
        accessToken == null
          ? undefined
          : { authorization: `Bearer ${accessToken}` },
      ),
  });
}

export function useGqlMutation<TResult, TVariables extends object | undefined>({
  mutation,
  ...options
}: Omit<UseMutationOptions<TResult, Error, TVariables>, "mutationFn"> & {
  mutation: TypedDocumentNode<TResult, TVariables>;
}) {
  const accessToken = useAccessTokenBasic();
  return useMutation({
    ...options,
    mutationFn: (variables: TVariables) =>
      request(
        config.publicApiUrl,
        mutation,
        variables,
        accessToken == null
          ? undefined
          : { authorization: `Bearer ${accessToken}` },
      ),
  });
}
